import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { LotteryRepository } from '../infrastructure/repositories/lottery.repository';
import { ClassActuality } from '../model/enums/class-actuality';
import { Lottery } from '../model/interfaces/lottery';
import { LotteryClass } from '../model/interfaces/lottery-class';
import { LotteryDraw } from '../model/interfaces/lottery-draw';
import { TicketPackage } from '../model/interfaces/ticket-package';

@Injectable()
export class LotteryService
{
    private readonly lotteryRepository: LotteryRepository;
    private cache: any = {};

    private currentLottery: Lottery;
    private currentLotteryDataSource: BehaviorSubject<Lottery>;
    private currentLottery$: Observable<Lottery>;
    private lotterySubjects: { [key: string]: ReplaySubject<ReadonlyArray<Lottery>>; } = {};

    /**
     * Short-lived cache for getSaleStop to deduplicate concurrent calls.
     *
     * The response contains a countdown (Days/Hours/Minutes/Seconds) that is time-relative,
     * so we use a short TTL (30s) to avoid stale data while still deduplicating
     * the 5+ concurrent calls that happen on the home page from hero components.
     *
     * Cache key is the string representation of the saleActuality parameter
     * (including 'undefined' when no argument is passed) to keep different
     * actuality requests separate.
     */
    private readonly saleStopCache = new Map<string, { observable: Observable<any>; expiry: number }>();
    private static readonly SALE_STOP_CACHE_TTL_MS = 30_000;

    public constructor(lotteryRepository: LotteryRepository)
    {
        this.lotteryRepository = lotteryRepository;
    }

    public getLotteriesByActuality(actuality?: number): Observable<ReadonlyArray<Lottery>>
    {
        const date = new Date();
        const cacheKey = `${actuality}-${date.getFullYear()}${date.getMonth()}${date.getDay()}`;

        if (this.lotterySubjects[cacheKey] != null)
        {
            return this.lotterySubjects[cacheKey].asObservable();
        }

        const lotterySubject = new ReplaySubject<ReadonlyArray<Lottery>>(1);
        this.lotterySubjects[cacheKey] = lotterySubject;

        this.lotteryRepository.getLotteriesByActuality(actuality).subscribe((
            lotteries
            ) =>
            {
                lotterySubject.next(lotteries);
            });

        return lotterySubject.asObservable();
    }

    public getLotteryClassById(lotteryClassId: number): Observable<LotteryClass>
    {
        const cacheKey = `lotteryclass_${lotteryClassId}`;
        const lotteryClassFromCache = this.cache[cacheKey] as LotteryClass;
        if (lotteryClassFromCache != null)
        {
            return of(lotteryClassFromCache);
        }

        return this.getLotteryClassesByLotteryActuality(7).pipe(
            map(
                (lotteryClasses: LotteryClass[]) =>
                {
                    const lotteryClass = lotteryClasses.find(lc => lc.Id === lotteryClassId);
                    this.cache[cacheKey] = lotteryClass;
                    return lotteryClass;
                }
            )
        );
    }

    /// Gets the current lottery
    public getCurrentLottery(): Observable<Lottery>
    {
        return this.getLotteriesByActuality(5).pipe(
            map(lotteries =>
            {
                this.currentLottery = lotteries.filter((item) => item.LotteryActuality === 1)[0];
                if (this.currentLotteryDataSource)
                {
                    this.currentLotteryDataSource.next(this.currentLottery);
                }
                return lotteries[0];
            })
        );
    }

    /// Gets the next lotteryClass where bs-purchase is enabled
    public getNextBsLotteryClass(): Observable<LotteryClass>
    {
        return this.getLotteriesByActuality(5).pipe(
            map(lotteries =>
            {
                const classes = lotteries[0].LotteryClasses.concat(lotteries[1].LotteryClasses);
                const nextBSLotteryClass = classes.filter((item) => item.ActualityByBSSale === 1)[0];
                return nextBSLotteryClass;
            })
        );
    }

    /// Gets the next lotteryClass where bs-purchase is enabled
    public getNextFmtLotteryClass(): Observable<LotteryClass>
    {
        return this.getLotteriesByActuality(5).pipe(
            map(lotteries =>
            {
                const classes = lotteries[0].LotteryClasses.concat(lotteries[1].LotteryClasses);
                const fmtLotteryClass = classes.filter((item) => item.ActualityByDKSale === 1)[0];
                return fmtLotteryClass;
            })
        );
    }

    private getLotteryClassesByLotteryActuality(actuality: number): Observable<LotteryClass[]>
    {
        const cacheKey = `lotteryclasses_${actuality}`;
        const lotteryClassesFromCache = this.cache[cacheKey] as LotteryClass[];

        if (lotteryClassesFromCache != null)
        {
            return of(lotteryClassesFromCache);
        }

        return this.getLotteriesByActuality(7).pipe(
            map((lotteries: Lottery[]) =>
            {
                const lotteryClasses = lotteries.reduce((lotteryClasses: LotteryClass[], lottery: Lottery) =>
                {
                    lotteryClasses = [...lotteryClasses, ...lottery.LotteryClasses];
                    return lotteryClasses;
                }, []);

                this.cache[cacheKey] = lotteryClasses;
                return lotteryClasses;
            })
        );

    }

    public getPackagesForSale(): Observable<ReadonlyArray<TicketPackage>>
    {
        return this.getLotteriesByActuality(5).pipe(
            map((lotteries) =>
            {
                const [lottery] = lotteries;
                return lottery.TicketPackages;
            })
        );
    }

    public getCurrentlotteryObservable(): Observable<Lottery>
    {
        if (this.currentLottery$)
        {
            return this.currentLottery$;
        }
        else
        {
            this.currentLotteryDataSource = new BehaviorSubject(this.currentLottery);
            this.currentLottery$ = this.currentLotteryDataSource.asObservable();
            this.getCurrentLottery();
            return this.currentLottery$;
        }
    }

    public getNextDrawDate(): Observable<Date>
    {
        let keepGoing = true;
        return this.getLotteriesByActuality(5).pipe(
            map(lotteries =>
            {
                const classes = lotteries[0].LotteryClasses.concat(lotteries[1].LotteryClasses);
                const today = new Date().getDate();
                let nextLotteryClassDraw: LotteryDraw;

                classes.forEach((item, classIndex) =>
                {
                    if (item.ActualityByDraw === 1)
                    {
                        // Find the draw in draws which is today
                        item.Draws.forEach((draw, drawIndex) => {

                            // keepGoing prop to stop iterating through entire collection if match found
                            if(keepGoing === true)
                            {
                                const drawDate = new Date(draw.DrawDate).getDate();

                                if(drawDate === today)
                                {
                                    // Use dates in millis to show correct countdown time on same day before 06:00
                                    const todayMilli = new Date().getTime();
                                    const drawDateMilli = new Date(draw.DrawDate).getTime();

                                    // If today is last draw date, get first draw of next class lottery
                                    if(drawIndex === 4)
                                    {
                                        if(todayMilli > drawDateMilli)
                                        {
                                            nextLotteryClassDraw = classes[classIndex + 1].Draws[0];
                                            keepGoing = false;
                                        }
                                        else
                                        {
                                            nextLotteryClassDraw = item.Draws[drawIndex];
                                            keepGoing = false;
                                        }
                                    }
                                    else
                                    {
                                        if(todayMilli > drawDateMilli)
                                        {
                                            nextLotteryClassDraw = item.Draws[drawIndex + 1];
                                            keepGoing = false;
                                        }
                                        else
                                        {
                                            nextLotteryClassDraw = item.Draws[drawIndex];
                                            keepGoing = false;
                                        }
                                    }
                                }
                                else
                                {
                                    nextLotteryClassDraw = item.Draws[0];
                                }
                            }
                        });
                    }
                });

                return new Date(nextLotteryClassDraw.DrawDate);
            })
        );
    }

    public getSaleStop(saleActuality?: ClassActuality): Observable<any>
    {
        const cacheKey = String(saleActuality);
        const now = Date.now();
        const cached = this.saleStopCache.get(cacheKey);

        if (cached && cached.expiry > now)
        {
            return cached.observable;
        }

        const saleStop$ = this.lotteryRepository.getSaleStop(saleActuality).pipe(
            shareReplay({ bufferSize: 1, refCount: false })
        );

        this.saleStopCache.set(cacheKey, {
            observable: saleStop$,
            expiry: now + LotteryService.SALE_STOP_CACHE_TTL_MS
        });

        return saleStop$;
    }

    public isInUrgencyPeriod(): Observable<boolean>
    {

        return this.getSaleStop().pipe(
            map(obj =>
            {
                // test case:
                // obj.Days = 4;

                if(obj.Days < 5)
                {
                    return true;
                }
                else{
                    return false;
                }
            })
        );
    }

    public getCreditCardSaleState(): Observable<boolean>
    {
        let creditCardSaleEnabled: boolean;
        return this.getLotteriesByActuality(ClassActuality.Last + ClassActuality.Current + ClassActuality.Next + ClassActuality.Future).pipe(map(lotteries => {
            lotteries.forEach((lottery: Lottery) => {
                lottery.LotteryClasses.forEach((lotClass) =>
                    {
                        if (lotClass.ActualityByDraw === ClassActuality.Current)
                        {
                            const dankortLastSale = +new Date(lotClass.DankortLastSale);
                            if (+new Date() > dankortLastSale)
                            {
                                creditCardSaleEnabled = false;
                            }
                            else
                            {
                                creditCardSaleEnabled = true;
                            }
                        }
                    });
            });
            return creditCardSaleEnabled;
        }));
    }
}
